//@author: mandy

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    NgZone,
    OnInit,
    Output,
    ViewEncapsulation,
} from "@angular/core";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { PreferenceService } from "../../../services/preference.service";
import { TelemetryService } from "../../../services/telemetry.service";

@TypeManagerDecorator("s25-ng-workflow")
@Component({
    selector: "s25-ng-workflow",
    template: `
        @if (this.init) {
            <div id="workflowSec" class="c-margin-left--half c-margin-top--half  c-margin-bottom--half ">
                <h2 class="c-margin-bottom--single">Workflow Settings</h2>
                Tier include:
                <ul>
                    <li>Notification Policy on Event Type/Organizations (approval required only)</li>
                    <li>Location Assignment then Location Notification</li>
                    <li>Resource Assignment then Resource Notification</li>
                    <li>Notification Policy generated by previous tasks (eg. Requirement Notification)</li>
                </ul>
                <div id="workflow">
                    <p class="ngBold c-margin-bottom-half">Use Tiered Workflow <br /><br /></p>
                    <s25-toggle-button
                        (valueChange)="this.onToggleChange($event)"
                        [value]="this.workflowChained"
                    ></s25-toggle-button>
                </div>
                <!--
            <div>
              <label class="c-displayBlock" for="confirmed">
                <input
                  type="checkbox"
                  id="confirmed"
                  [checked]="workflowConfirmComplete"
                  (change)="onChange($event, 'complete')"
                  />
                  Change Event State to "Confirmed" when all tasks are complete
                </label>
              </div>
              <div>
                <label class="c-displayBlock" for="cancelled">
                  <input
                    type="checkbox"
                    id="cancelled"
                    [checked]="workflowCancelDenied"
                    (change)="onChange($event, 'cancelled')"
                    />
                    Change Event State to "Cancelled" when any tasks are denied
                  </label>
                </div>
                -->

                <!-- Waitlist Feature Flag -->
                <div id="waitlist" class="c-margin-top--double">
                    <p class="ngBold c-margin-bottom-half">Enable Waitlist</p>
                    <s25-toggle-button
                        (valueChange)="onToggleWaitlistChange($event, 'waitlist')"
                        [value]="waitlistEnabled"
                    ></s25-toggle-button>
                </div>

                <div class="c-displayBlock c-padding-bottom--double">
                    <button
                        class="aw-button aw-button--primary c-margin-top--single c-margin-right--quarter"
                        (click)="save()"
                    >
                        {{ this.loading ? "Saving...." : "Save" }}
                    </button>
                    <button class="aw-button aw-button--outline c-margin-top--single" (click)="cancel()">Cancel</button>
                </div>
            </div>

            @if (message !== "") {
                <div class="ngGreen ngBold cn-alert cn-alert--success c-margin-bottom--single" role="alert">
                    <div class="cn-alert__icon cn-icon" name="alert--info">
                        <s25-ng-icon [type]="'check'" [ariaLabel]="false" />
                    </div>
                    <div class="cn-alert__label">
                        <span>{{ message }}</span>
                    </div>
                </div>
            }
        }
    `,
    styles: [],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class S25WorkflowComponent implements OnInit {
    @Output() modelValueChange = new EventEmitter();
    init: boolean = false;
    loading: boolean = false;
    workflowConfirmComplete: boolean = false;
    workflowCancelDenied: boolean = false;
    workflowChained: boolean = false;
    waitlistEnabled: boolean = true; // default true
    initLoading: boolean = false;
    initWorkflowConfirmComplete: boolean = false;
    initWorkflowCancelDenied: boolean = false;
    initWorkflowChained: boolean = false;
    initWaitlistEnabled: boolean = true;

    message: string = "";

    constructor(
        private elementRef: ElementRef,
        private zone: NgZone,
        private cd: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns
    }

    ngOnInit() {
        this.elementRef.nativeElement.angBridge = this;
        this.getPrefs();
        this.cd.detectChanges();
    }

    getPrefs() {
        return S25Util.all({
            prefs: PreferenceService.getPreferences(
                ["workflowChained", "workflowConfirmComplete", "workflowCancelDenied", "25L_sp_waitlist"],
                "S",
            ),
        }).then((resp) => {
            this.initWorkflowChained = this.workflowChained = S25Util.toBool(resp.prefs.workflowChained.value);
            this.initWorkflowConfirmComplete = this.workflowConfirmComplete = S25Util.toBool(
                resp.prefs.workflowConfirmComplete.value,
            );
            this.initWorkflowCancelDenied = this.workflowCancelDenied = S25Util.toBool(
                resp.prefs.workflowCancelDenied.value,
            );
            this.initWaitlistEnabled = this.waitlistEnabled = S25Util.toBool(resp.prefs["25L_sp_waitlist"].value);

            this.initLoading = false;
            this.init = true;
            this.cd.detectChanges();
        });
    }

    onChange(event: any, name: string) {
        if (name === "complete") {
            this.workflowConfirmComplete = event.target.checked;
        } else {
            this.workflowCancelDenied = event.target.checked;
        }
    }

    onToggleChange(event: any) {
        this.workflowChained = event;
        TelemetryService.sendWithSub("SysSettings", "Wrkflw", "Tiered" + (this.workflowChained ? "On" : "Off"));
        this.cd.detectChanges();
    }

    onToggleWaitlistChange(event: boolean) {
        this.waitlistEnabled = event;
        TelemetryService.sendWithSub("SysSettings", "Wrkflw", "Waitlist" + (this.waitlistEnabled ? "On" : "Off"));
    }

    save() {
        this.loading = true;
        this.cd.detectChanges();

        const promiseArr = [];
        this.initWorkflowChained !== this.workflowChained &&
            promiseArr.push(
                PreferenceService.setPreference("workflowChained", S25Util.bool.toChar(this.workflowChained), "S"),
            );

        this.initWaitlistEnabled !== this.waitlistEnabled &&
            promiseArr.push(
                PreferenceService.setPreference("25L_sp_waitlist", S25Util.bool.toChar(this.waitlistEnabled), "S"),
            );

        /*
        this.initWorkflowConfirmComplete !== this.workflowConfirmComplete &&
            promiseArr.push(PreferenceService.setPreference("workflowConfirmComplete", workflowConfirmComplete, "S"));

        this.initWorkflowCancelDenied !== this.workflowCancelDenied &&
            promiseArr.push(PreferenceService.setPreference("workflowCancelDenied", workflowCancelDenied, "S"));

         */

        if (promiseArr) {
            return S25Util.all(promiseArr).then((resp) => {
                this.loading = false;
                this.message = "Success!";
                this.getPrefs();
                this.cd.detectChanges();
            });
        }
    }

    cancel() {
        this.getPrefs();
        this.loading = false;
        this.cd.detectChanges();
    }
}
